/**
 * Estados de la Gestión de Ingreso: las tres etapas que siguen a Validados
 * (masivo) / Seleccionados (profesional) en el flujo de Selección.
 *
 *   CITADO -> ASISTE -> INGRESO_EFECTIVO
 *
 * El valor es el `Nombre` de BusPerEstado en la base; la etiqueta es lo que se
 * lee en pantalla. El último se llama INGRESO_EFECTIVO y no INGRESADO porque
 * INGRESADO ya existe en la base: es el estado inicial con el que una persona
 * entra a un proceso, y no tiene nada que ver con esta etapa.
 *
 * Vive en un solo lugar porque lo usan la tabla, el filtro, los chips de
 * filtros activos y el modal de avanzar: si cada uno declarara sus rótulos,
 * bastaría con cambiar uno para que digan cosas distintas del mismo registro.
 */
export const ESTADO_CITADO = "CITADO";
export const ESTADO_ASISTE = "ASISTE";
export const ESTADO_INGRESO_EFECTIVO = "INGRESO_EFECTIVO";

export const ESTADOS_GESTION_INGRESO: string[] = [
  ESTADO_CITADO,
  ESTADO_ASISTE,
  ESTADO_INGRESO_EFECTIVO,
];

export interface IEstadoGestionIngresoConfig {
  label: string;
  bg: string;
  color: string;
  icon: string;
}

const ESTADO_CONFIG: Record<string, IEstadoGestionIngresoConfig> = {
  [ESTADO_CITADO]:           { label: "Citado",    bg: "#eef4ff", color: "#0065dd", icon: "event"          },
  [ESTADO_ASISTE]:           { label: "Asistente", bg: "#fff4e5", color: "#8a5a00", icon: "how_to_reg"     },
  [ESTADO_INGRESO_EFECTIVO]: { label: "Ingresado", bg: "#e7f5ec", color: "#1e7a45", icon: "task_alt"       },
};

const ESTADO_DESCONOCIDO: IEstadoGestionIngresoConfig = {
  label: "Sin estado",
  bg: "#f1f5f9",
  color: "#64748b",
  icon: "help",
};

export const resolveEstadoGestionIngreso = (
  estado: string | null | undefined
): IEstadoGestionIngresoConfig => (estado && ESTADO_CONFIG[estado]) || ESTADO_DESCONOCIDO;

/** Etiqueta del estado al que avanza un registro, o null si ya está al final. */
export const siguienteEstadoGestionIngreso = (
  estado: string | null | undefined
): string | null => {
  const posicion = ESTADOS_GESTION_INGRESO.indexOf(estado ?? "");
  if (posicion < 0 || posicion === ESTADOS_GESTION_INGRESO.length - 1) return null;
  return resolveEstadoGestionIngreso(ESTADOS_GESTION_INGRESO[posicion + 1]).label;
};

/** Etiqueta del estado al que retrocede un registro (Citado vuelve a Selección). */
export const anteriorEstadoGestionIngreso = (
  estado: string | null | undefined,
  etapaSeleccion: string
): string => {
  const posicion = ESTADOS_GESTION_INGRESO.indexOf(estado ?? "");
  if (posicion <= 0) return etapaSeleccion;
  return resolveEstadoGestionIngreso(ESTADOS_GESTION_INGRESO[posicion - 1]).label;
};

/** Opciones del filtro "Estado" (formato etiqueta/valor de StateFiltro). */
export const OPCIONES_FILTRO_GESTION_INGRESO = ESTADOS_GESTION_INGRESO.map((valor) => ({
  etiqueta: resolveEstadoGestionIngreso(valor).label,
  valor,
}));

/**
 * Estados a pedir al backend según el filtro: los marcados, o los tres si no
 * hay ninguno (o si llega algo que no es un estado de esta etapa).
 */
export const estadosAConsultar = (filtroEstado: unknown): string[] => {
  const marcados = Array.isArray(filtroEstado)
    ? ESTADOS_GESTION_INGRESO.filter((estado) => filtroEstado.includes(estado))
    : [];
  return marcados.length > 0 ? marcados : ESTADOS_GESTION_INGRESO;
};

/**
 * Gestión de Ingreso oculta en la UI (pestaña de Selección y botón "Gestionar
 * Ingreso"). Cambiar a true para volver a mostrarla.
 */
export const GESTION_INGRESO_VISIBLE = false;
